import { useState, useEffect, Fragment, useRef } from 'react';
import translator from '@Components/Utilities/Translator';
import TranslationText from '@Components/Utilities/TranslationText';
import PopularBrands from '@Components/Home/PopularBrands';
import DoubleClickAd from '@Components/Shared/DoubleClick/DoubleClickAd';
import DrillDownSearch from '@Components/Home/DrillDownSearch';
import PartsQuickSearch from '@Components/Home/PartsQuickSearch';
import PartsSearch from '@Components/Shared/PartsSearch';
import AboutUs from '@Components/Home/AboutUs';
import CategoryAccordionSection from '@Components/Home/CategoryAccordionSection';
import CategoryIconGrid from '@Components/Home/CategoryIconGrid';
import RecentBlogs from '@Components/Home/RecentBlogs';
import NewsStand from '@Components/Home/NewsStand';
import ConditionalWrapper from '@Components/Utilities/ConditionalWrapper';
import { bannerAdPositions } from '@Components/Utilities/SandhillsConstants';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import { getBannerAd, chunkArray, chunkByBreakingIndexes, shouldOpenInNewTab, criteoPushEvent, sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import SandhillsAccordion from '@Components/MuiWrappers/SandhillsAccordion';
import FirstTimeUserInstructionBanner from '@Components/Home/FirstTimeUserInstructionBanner';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';
import ListingCarousel from '@Components/Shared/ListingCarousel';
import EventsSection from '@Components/Home/EventsSection';
import FeaturedEvents from '@Components/Home/FeaturedEvents';
import RequestAQuote from '@Components/Home/RequestAQuote';
import QuoteInstructions from '@Components/Home/QuoteInstructions';
import FullBlogSection from '@Components/Home/FullBlogSection';
import DrilldownTabPanel from '@Components/Home/DrilldownTabPanel';
import RequestAQuoteBanner from '@Components/Home/RequestAQuoteBanner';
import RequestACharter from '@Components/Home/RequestACharter';
import FeaturedSellerAuctionDate from '@Components/Events/FeaturedSellerAuctionDate';
import AuctionBoardLiveBoardHeader from '@Components/AuctionBoard/AuctionBoardLiveBoardHeader';
import Backlinks from '@Components/Home/Backlinks';


const HomePageView = (props) => {
    const requestAQuoteBannerRef = useRef(null);
    const windowSize = useWindowSize();
    const mainCategoryOptions = Object.keys(props.HomePageCategories);
    const [showMoreAdditionalCategories, setShowMoreAdditionalCategories] = useState(props.ShowAllAdditionalHomePageCategoriesOnMobileByDefault);
    const [selectedCategoryGrid, setSelectedCategoryGrid] = useState(mainCategoryOptions[0]);
    const [categoryGroupID, setCategoryGroupID] = useState(mainCategoryOptions[0]?.[0]?.GroupNameID);
    const currentUser = useRecoilValue(atoms.currentUser);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const routes = useRecoilValue(atoms.routes);
    const [slideIndex, setSlideIndex] = useState();
    const [hideDisplayHPCs, setHideDisplayHPC] = useState(props.HideDisplayHomePageCategories);
    const displayCatsOnly = props.DisplayCatIconAlone && !hideDisplayHPCs;
    const isRY = currentSite?.Site?.IsRentalYard;

    const slideIndexCallback = (index) => {
        setSlideIndex(index);
    };

    const handleRequestAQuoteBannerClick = () => {
        requestAQuoteBannerRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(function setIsAuctionSearch() {
        if (props.DrillDownSearch && props.DrillDownSearch.ShowSearchForm && props.HomePageCategories) {
            setCategoryGroupID(props.HomePageCategories[selectedCategoryGrid][0]?.GroupNameID);
        }
    }, [selectedCategoryGrid]);

    useEffect(() => {
        if (props.ShouldPushCriteoEvent) {
            criteoPushEvent("viewHome");
        }

        if (props.ScrollToRAQ) {
            requestAQuoteBannerRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, []);

    const getListPageUrl = (routeUrl, categorySlug, categoryId) => {
        if (shouldOpenInNewTab(routeUrl)) return routeUrl;
        if (!('' + routeUrl).includes('/' + props.DefaultListingTypeSlug + '/')) return routeUrl;
        var listings = translator.translate('sandhills.tradepubwebsite.mvc.listings').toLowerCase();
        var url = listings + '/' + props.listingTypes.currentType + '/';
        if (categorySlug) url = url + categorySlug + '/';
        if (categoryId) url = url + categoryId;
        return url;
    };

    const renderCategoryLink = (cat, isParent, useNormalFontForParents) => {
        let wrapper = (children) => children;
        const hLevel = cat.HTagLevel ? cat.HTagLevel : cat.Children?.HTagLevel;
        switch (hLevel) {
            case 3:
                wrapper = (children) => <h3>{children}</h3>;
                break;
            case 4:
                wrapper = (children) => <h4>{children}</h4>;
                break;
            case 5:
                wrapper = (children) => <h5>{children}</h5>;
                break;
            case 6:
                wrapper = (children) => <h6>{children}</h6>;
                break;
        }

        return (
            <ConditionalWrapper condition={hLevel} wrapper={wrapper}>
                <a className='links-link' href={cat.RawURL}>
                    {(isParent && !useNormalFontForParents) ?
                        <b dangerouslySetInnerHTML={{ __html: cat.Name }} /> :
                        <span dangerouslySetInnerHTML={{ __html: cat.Name }} />}
                </a>
            </ConditionalWrapper>
        );
    };

    const createDrilldown = (key, fullCategoryList, numColumns, className, removeChildren, useNormalFontForParents, useUnorderedList) => {
        if (!numColumns) numColumns = 1;
        var list = [];

        /*truck-mobiles.nl breaking at indexes of specific categoryIDs*/
        let colBreakIndexes = fullCategoryList.map((e, i) => e.isColumnBreaker ? i : undefined).filter(x => x);
        if (colBreakIndexes.length > 0) {
            list = chunkByBreakingIndexes(fullCategoryList, colBreakIndexes);
        }

        if (list.length <= 0) {
            list = chunkArray(fullCategoryList, numColumns, removeChildren);
        }

        return list.map((categoryList, idx) => {
            return (
                <Fragment {...(key ? { key: `${key}-list-${idx}-${categoryList.length}` } : {})}>
                    {useUnorderedList ?
                        <ul className={className}>
                            {createDrilldownLinks(`${key}-list${idx}-ul`, categoryList, useNormalFontForParents, true)}
                        </ul> :
                        <div className={className}>
                            {createDrilldownLinks(`${key}-list${idx}-div`, categoryList, useNormalFontForParents, useUnorderedList)}
                        </div>}
                </Fragment>
            );
        });
    };

    const createDrilldownLinks = (key, categoryList, useNormalFontForParents, useUnorderedList) => {
        return categoryList.map((cat, idx) => {
            let isParent = (cat.Children && cat.Children.length > 0);
            let numColumns = isParent ? cat.Columns : 0;
            let children = chunkArray(cat.Children || [], numColumns, null);
            return (
                <Fragment {...(key ? { key: `${key}-cat-${idx}` } : {})}>
                    {useUnorderedList ?
                        <li key={`li-${cat.Name}-i`} className={'links-container'}>
                            {renderCategoryLink(cat, isParent, useNormalFontForParents)}
                            {isParent && children.map(child => createDrilldown(`${key}-dd-li`, child, 1, 'links-container child', true, false, true))}
                        </li> :
                        <div key={`div-${cat.Name}-i`} className={'links-container' + ((isParent && children.length == 2) ? ' double-wide' : '')}>
                            {renderCategoryLink(cat, isParent, useNormalFontForParents)}
                            {isParent &&
                                <div className='links-container child'>
                                    {children.map(child => createDrilldown(`${key}-dd-div`, child, 1, 'sub-link-container'))}
                                </div>}
                        </div>}
                </Fragment>
            );
        });
    };

    const toggleShowMoreAdditionalCategories = () => {
        setShowMoreAdditionalCategories(showMoreAdditionalCategories => !showMoreAdditionalCategories);
    };

    const middleBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Middle);
    const topBannerAd = getBannerAd(props.DoubleClickAdCollection?.AdUnits, bannerAdPositions.Top);

    const getAccordionModelBySection = (sectionName) => {
        if (sectionName == "RentalCategories") {
            return props.RentalAccordions.find(({ AccordionSection }) => AccordionSection == sectionName);
        }
        return props.Accordions.find(({ AccordionSection }) => AccordionSection == sectionName);
    };

    const getCategoryIconGrid = (useH3) => {
        return (
            <section className={hideDisplayHPCs ? 'hide' : 'category-grid'}>
                {props.PageHeader && (<h1>{props.PageHeader}</h1>)}
                {props.DrillDownSearch && props.DrillDownSearch.ShowSearchForm && !props.DrillDownSearch.ShowDrilldownSearchOnBottom &&
                    <DrillDownSearch
                        {...props.DrillDownSearch}
                        SetSlideIndex={slideIndexCallback}
                        CategoryGroupID={categoryGroupID}
                        bgVariant='white'
                        CurrentUserFormattedAddress={props.LocationString}
                        IsRY={isRY}

                    />}
                {mainCategoryOptions.length > 1 &&
                    <div className="tab-buttons cf">
                        {mainCategoryOptions.map((categoryName) =>
                            categoryName !== ("Production Sales") ?
                                (
                                    <a role='button' tabIndex='0' onKeyDown={(e) => { if (e.key === "Enter" || e.key === ' ') { e.preventDefault(); setSelectedCategoryGrid(categoryName); } }} className={"tab-button " + (selectedCategoryGrid === categoryName ? "active" : '')} onClick={() => setSelectedCategoryGrid(categoryName)} key={`a-tab-${categoryName}`}>
                                        <span>{categoryName}</span>
                                    </a>
                                ) :
                                (
                                    <a role='button' href="/events/upcoming" tabIndex='0' className={"tab-button " + (selectedCategoryGrid === categoryName ? "active" : '')} key={`a-tab-${categoryName}`}>
                                        <TranslationText key='production-sales' tKey='sandhills.productionsales' />
                                    </a>
                                )
                        )}
                    </div>
                }
                {mainCategoryOptions.map((categoryName) =>
                    <div key={'ig-mc-' + categoryName} className={'categories' + (categoryName == selectedCategoryGrid ? '' : ' hide') + (windowSize.isMobile && categoryName == 'Popular Categories' ? ' nowrap' : '')}>
                        <CategoryIconGrid categories={props.HomePageCategories[categoryName]} isMobile={windowSize.isMobile} hpcColumnOverride={props.HPCColumnOverride} useH3={useH3} />
                    </div>)}
                {props.DrillDownSearch && props.DrillDownSearch.ShowSearchForm && props.DrillDownSearch.ShowDrilldownSearchOnBottom &&
                    <DrillDownSearch
                        {...props.DrillDownSearch}
                        SetSlideIndex={slideIndexCallback}
                        CategoryGroupID={categoryGroupID}
                        bgVariant='white'
                        CurrentUserFormattedAddress={props.LocationString}
                        IsRY={isRY}

                    />}
                {props.ShowHomePagePartsSearch && !windowSize.isMobile &&
                    <PartsQuickSearch
                        searchUrl={props.PartsSearchURL}
                        showSearchTypes
                        bgVariant='white'
                    />}
            </section>
        );
    };

    const renderSection = (sectionName) => {
        switch (sectionName) {
            case 'FeaturedEvents':
                return (props.FeaturedEvents?.Cards?.length > 0 && !displayCatsOnly ?
                    <FeaturedEvents
                        {...props.FeaturedEvents}
                    /> : <></>
                );
            case 'AdditionalCats':
                return ((props.AdditionalCats && props.AdditionalCats.length > 0) && !displayCatsOnly ?
                    <section className={'drilldown-section' + (!windowSize.isMobile ? ' mat-grey' : '') + (!windowSize.isMobile && props.HasVerticalAdditionalCategoryLayout ? ' additional-categories-section' : '')}>
                        {windowSize.isMobile && !props.UseAlternateAdditionalCategorySection &&
                            <div className='va-container'>
                                <button className='button ts-button' id='btnmAdditionalCategories' onClick={toggleShowMoreAdditionalCategories}>
                                    {showMoreAdditionalCategories ?
                                        <TranslationText key='hide-mobile' tKey='sandhills.closeadditionalcategories' /> :
                                        <TranslationText key='show-mobile' tKey='sandhills.showadditionalcategories' />}
                                </button>
                            </div>}
                        {!props.UseCategoryAccordion || props.UseAlternateAdditionalCategorySection ?
                            <div className={'content ' + (windowSize.isMobile && !showMoreAdditionalCategories && !props.UseAlternateAdditionalCategorySection ? 'hidden' : '')}>
                                <h2 className='section-title'>
                                    <span className='section-title-text'>
                                        <TranslationText tKey='sandhills.additionalcategories' />
                                    </span>
                                </h2>
                                <div className={'section-container' + (windowSize.isMobile ? ' additional-categories' : '')}>
                                    {!windowSize.isMobile ?
                                        <>
                                            {props.HasVerticalAdditionalCategoryLayout ?
                                                <>
                                                    {/**
                                                * FYI - This block is currently only in use for beta.MachineryTrader.com.
                                                * The logic here should be disregarded when considering other sites.
                                                * Please redo logic when/if other sites adopt a new homepage additional category layout.
                                                * A lot of the homepage site settings can be removed completely once that happens and the logic here can be simplified.
                                                * **/}
                                                    {props.AdditionalCats.map((additionalCat, idx) =>
                                                        <div key={`${additionalCat.ListTitle}-${idx}`} className={'sub-section-container' + (additionalCat.columns === 1 || (additionalCat.Categories && additionalCat.Categories.length === 1) ? ' sub-section-single-col' : '')}>
                                                            {additionalCat.ShowListTitle && additionalCat.ListTitle &&
                                                                <h3 className='sub-section-title'>
                                                                    <span>{additionalCat.ListTitle}</span>
                                                                </h3>}
                                                            <div className={'sub-section-content' + (props.AdditionalCats.length === 1 ? ' sub-section-space-between' : '')}>
                                                                {additionalCat.Categories &&
                                                                    createDrilldown(`dd-${additionalCat.ListTitle}-sub${idx}`, additionalCat.Categories, additionalCat.columns, 'links', false, true)}
                                                            </div>
                                                        </div>)}
                                                </> :
                                                <>
                                                    {props.AdditionalCats.map((additionalCat, idx) =>
                                                        <div key={`${additionalCat.ListTitle}-${idx}`} className={'sub-section-container' + (additionalCat.columns === 1 || (additionalCat.Categories && additionalCat.Categories.length === 1) ? ' sub-section-single-col' : '')}>
                                                            {additionalCat.ShowListTitle && additionalCat.ListTitle &&
                                                                <h3 className='sub-section-title'>
                                                                    <span>{additionalCat.ListTitle}</span>
                                                                </h3>}
                                                            <div className={'sub-section-content' + (props.AdditionalCats.length === 1 ? ' sub-section-space-between' : '')}>
                                                                {additionalCat.Categories && createDrilldown(`dd-${additionalCat.ListingTitle}-sub${idx}`, additionalCat.Categories, additionalCat.columns, 'links')}
                                                            </div>
                                                        </div>)}
                                                </>}
                                        </> :
                                        props.MobileAdditionalCats.map((additionalCat, idx) =>
                                            <div key={'mac-' + additionalCat.ListTitle} className='sub-section-container'>
                                                {additionalCat.ShowListTitle && additionalCat.ListTitle &&
                                                    <h3 className='sub-section-title'>
                                                        <span>{additionalCat.ListTitle}</span>
                                                    </h3>}
                                                <div className='sub-section-content'>
                                                    {createDrilldown(`mac-dd-${additionalCat.ListingTitle}-sub${idx}`, additionalCat.Categories, additionalCat.columns, 'links', false, props.HasVerticalAdditionalCategoryLayout)}
                                                </div>
                                            </div>)}
                                </div>
                            </div> :
                            <div className='rental-section'>
                                <div className='content'>
                                    <h2 className='section-title'>
                                        <span className='section-title-text'>
                                            <TranslationText tKey='sandhills.additionalcategories' />
                                        </span>
                                    </h2>
                                    {props.AdditionalCats.map((additionalCat) =>
                                        <SandhillsAccordion className='' accordionTitle={additionalCat.ListTitle} categories={additionalCat} mapDetails={false} key={`accordion-${additionalCat.ListTitle}`} />)}
                                </div>
                            </div>
                        }
                    </section > : <></>
                );
            case 'CategoryGrid':
                return (
                    <>
                        {!currentSite?.Site?.IsAuctionTime && (windowSize.isMobile && ((props.UseAlternateAdPlacement && topBannerAd) || (!props.UseAlternateAdPlacement && middleBannerAd))) &&
                            <DoubleClickAd
                                {...props.DoubleClickAdCollection}
                                position={props.UseAlternateAdPlacement ? bannerAdPositions.Top : bannerAdPositions.Middle}
                                ad={props.UseAlternateAdPlacement ? topBannerAd : middleBannerAd}
                                contained
                            />}
                        <div className={' primary-categories' + (!props.ExcludeBackgroundImage ? ' hero-image' : '')}>
                            {(!windowSize.isMobile && props.UseAlternateAdPlacement && topBannerAd) &&
                                <DoubleClickAd
                                    {...props.DoubleClickAdCollection}
                                    position={bannerAdPositions.Top}
                                    ad={topBannerAd}
                                    contained
                                />}
                            {getCategoryIconGrid()}
                        </div>
                    </>
                );
            case 'LiveAuctions':
                return (props.LiveEvents?.Events?.length > 0 && !displayCatsOnly ?
                    <EventsSection
                        {...props.LiveEvents}
                        CAPrivacyPolicyNotice={props.CAPrivacyPolicyNotice}
                        NotificationSettings={props.NotificationSettings}
                    /> : <></>
                );
            case 'UpcomingAuctions':
                return (props.UpcomingEvents?.Events?.length > 0 && !displayCatsOnly ?
                    <EventsSection
                        {...props.UpcomingEvents}
                        CAPrivacyPolicyNotice={props.CAPrivacyPolicyNotice}
                        NoLiveEvents={props.LiveEvents?.Events?.length <= 0}
                        NotificationSettings={props.NotificationSettings}
                    /> : <></>
                );
            case 'AuctionsForYou':
                return (!displayCatsOnly && props.RecommendedListings?.Cards?.length > 3 && <ListingCarousel {...props.RecommendedListings} translationKey={(currentSite.Site.IsBidcaller || currentSite.Site.IsAuctionTime) ? 'sandhills.auctionsforyou' : 'sandhills.recommendedforyou'} />);
            case 'RequestAQuoteBanner':
                return (
                    <RequestAQuoteBanner handleRequestAQuoteBannerClick={handleRequestAQuoteBannerClick} />
                );
            case 'BannerAd':
                return (!currentSite?.Site?.IsAuctionTime && (windowSize.isMobile || props.UseAlternateAdPlacement) && !displayCatsOnly ? <></> :
                    (middleBannerAd ?
                        <DoubleClickAd
                            {...props.DoubleClickAdCollection}
                            position={bannerAdPositions.Middle}
                            ad={middleBannerAd}
                            contained
                        /> : <></>)
                );
            case 'Hidden':
                return ((windowSize.isMobile || props.RecommendedListings?.Cards?.length > 3) !== currentSite.Site.IsRentalYard ?
                    <></> : <section className='hidden'></section>
                );
            case 'PopularBrands':
                return (
                    <>
                        {props.PopularBrands && !displayCatsOnly &&
                            <PopularBrands {...props.PopularBrands} HasSplitHomePageDrilldownCatTabs={props.DrillDownSearch.HomePageSplitDrilldownCategoryTabs ? true : false} SlideIndex={slideIndex} />}
                        {(middleBannerAd && props.UseAlternateAdPlacement) &&
                            <DoubleClickAd
                                {...props.DoubleClickAdCollection}
                                position={bannerAdPositions.Middle}
                                ad={middleBannerAd}
                                contained
                            />}
                    </>
                );
            case 'ExploreOurFamilyBrands':
                return (
                    <>
                        {props.Backlinks && <Backlinks {...props.Backlinks} />}
                    </>
                );
            case 'Parts-Attachments-Dismantled': {
                const attachmentAccordionModel = getAccordionModelBySection('Attachments');
                return (props.ShowPartsAttachmentsDismantledSection && !displayCatsOnly ?
                    <section className={'drilldown-section' + (!windowSize.isMobile ? ' mat-grey' : '')}>
                        <div className='content' id='attachments'>
                            <h2 className='section-title'>
                                <span className='section-title-text'>
                                    {windowSize.isMobile ?
                                        props.PartsAttachmentsDismantledHeader :
                                        <span dangerouslySetInnerHTML={{ __html: props.PartsAttachmentsDismantledHeader }}></span>}
                                </span>
                            </h2>
                            {windowSize.isMobile ?
                                <>
                                    <div className='divPartsDismantled'>
                                        {props.ShowHomePagePartsSearch &&
                                            <PartsSearch searchURL={props.PartsSearchURL} />}
                                    </div>
                                    <div className='divPartsDismantled'>
                                        {props.DismantledSearch && props.DismantledSearch.ShowSearchForm &&
                                            <DrillDownSearch DismantledSearch {...props.DismantledSearch} Mobile bgVariant='white' />}
                                    </div>
                                </> :
                                <div className='divPartsDismantled'>
                                    {props.ShowHomePagePartsSearch &&
                                        <PartsSearch searchURL={props.PartsSearchURL} />}
                                    {props.DismantledSearch && props.DismantledSearch.ShowSearchForm &&
                                        <DrillDownSearch DismantledSearch {...props.DismantledSearch} bgVariant='white' />}
                                </div>}
                            {(props.AttachmentCategories?.length > 0 || attachmentAccordionModel?.AccordionCats?.length > 0) &&
                                <div className={'attachment-cats'}>
                                    <strong className='part-label'>
                                        {props.AttachmentTerm}
                                    </strong>
                                    {
                                        attachmentAccordionModel?.AccordionCats?.length > 0 ?
                                            <CategoryAccordionSection {...attachmentAccordionModel} />
                                            :
                                            <div className={'section-container' + ((windowSize.isMobile || (!windowSize.isMobile && props.AttachmentCategories.length > 1)) ? '' : ' attachment-categories')}>
                                                {props.AttachmentCategories.map((attachmentCat, idx) =>
                                                    <div key={`pac-${attachmentCat.ListingTitle}-${idx}`} className={'sub-section-container' + (attachmentCat.Categories.length == 1 ? ' drilldwn-auto-width' : '')}>
                                                        {attachmentCat.Categories.length > 1 || (attachmentCat.Categories.length == 1 && (!attachmentCat.Categories[0].Children || attachmentCat.Categories[0].Children.length <= 0)) ?
                                                            <div className='sub-section-content'>
                                                                {attachmentCat.Categories ? createDrilldown(`pac-dd-${attachmentCat.ListingTitle}-sub${idx}`, attachmentCat.Categories, null, 'auto-col', true, windowSize.isMobile, !windowSize.isMobile) : null}
                                                            </div> :
                                                            <div className='links attachment-component-links'>
                                                                <h3>
                                                                    <a className='links-link header-section-link' href={getListPageUrl(attachmentCat.Categories[0].RawURL, attachmentCat.Categories[0].CategoryNameSlug, attachmentCat.Categories[0].categoryID)}>
                                                                        <span dangerouslySetInnerHTML={{ __html: attachmentCat.Categories[0].Name }}></span>
                                                                    </a>
                                                                </h3>
                                                                <div className='sub-section-content'>
                                                                    <div className='links-container multi-column'>
                                                                        {attachmentCat.Categories[0].Children ? createDrilldown(`pac-dd-${attachmentCat.ListingTitle}-sub${idx}`, attachmentCat.Categories[0].Children, attachmentCat.columns, 'links-container child') : ''}
                                                                    </div>
                                                                </div>
                                                            </div>}
                                                    </div>)}
                                            </div>
                                    }
                                </div>}
                        </div>
                    </section> : <></>
                );
            }
            case 'LatestListings':
                return (!displayCatsOnly && <ListingCarousel {...props.LatestListings} translationKey='sandhills.latestaddedlistings' />);
            case 'MarketRelevantSpecs':
                return ((props.MarketRelevantSpecs && props.MarketRelevantSpecs.length > 0) ?
                    <section className={'drilldown-section' + (!windowSize.isMobile ? ' mat-grey additional-categories-section' : '')}>
                        <div className='content' id='specs'>
                            <h2 className='section-title'>
                                <span className='section-title-text'>
                                    <span dangerouslySetInnerHTML={{ __html: props.MarketRelevantSpecsHeader }} />
                                </span>
                            </h2>
                            <div className='section-container'>
                                {props.MarketRelevantSpecs.map((relevantSpec, idx) =>
                                    <div key={`${relevantSpec.ListTitle}-${idx}`} className='sub-section-container'>
                                        {relevantSpec.ListTitle &&
                                            <h3 className='sub-section-title'>
                                                <span dangerouslySetInnerHTML={{ __html: relevantSpec.ListTitle }} />
                                            </h3>}
                                        {relevantSpec.Categories &&
                                            <div className='sub-section-content'>
                                                {(relevantSpec.columns == 1 ?
                                                    createDrilldownLinks(`ddl-${relevantSpec.ListTitle}-sub${idx}`, relevantSpec.Categories, true) :
                                                    createDrilldown(`dd-${relevantSpec.ListingTitle}-sub${idx}`, relevantSpec.Categories, relevantSpec.columns, 'links', false, true))}
                                            </div>}
                                    </div>)}
                            </div>
                        </div>
                    </section> : <></>
                );
            case 'RequestAQuote':
                return (
                    <>
                        <section className='request-a-quote-section'>
                            <RequestAQuote ref={requestAQuoteBannerRef} {...props.RequestAQuote} />
                        </section>
                    </>
                );
            case 'RequestACharter':
                return (
                    <>
                        <section className='request-a-charter-section'>
                            <RequestACharter {...props.RequestACharter} />
                        </section>
                    </>
                );
            case 'AboutUs':
                return (
                    <>
                        {!currentSite?.IsMobileAppContext && !displayCatsOnly &&
                            <>
                                <section className='about-us-section'>
                                    <ConditionalWrapper condition={!windowSize.isMobile} wrapper={children => <div className='about-us-outside-container'>{children}</div>}>
                                        <AboutUs {...props.AboutUs} className={windowSize.isMobile ? '' : 'about-us-container'} GrainMarketModel={props.GrainMarketModel} MobileDownload={props.MobileDownload} />
                                        {!props.UseFullBlogSection && <RecentBlogs {...props.RecentBlogs} maxNumBlogs={windowSize.isMobile ? 1 : false} />}
                                        {windowSize.isMobile && <div className='cf' />}
                                        {props.NewsStand && <NewsStand {...props.NewsStand} className={windowSize.isMobile ? '' : 'right'} />}
                                    </ConditionalWrapper>
                                </section>
                                {props.UseFullBlogSection && <section> <FullBlogSection {...props.RecentBlogs} /></section>}
                            </>
                        }
                    </>
                );
            case 'Jobs':
                return (
                    <section className='jobs-section'>
                        <div className='jobs-container'>
                            <h2>Looking for work in the aviation industry?</h2>
                            <a href='/job-listings'><strong>View Our Job Postings</strong></a>
                        </div>
                    </section>
                );
            case 'Accordion':
                return <CategoryAccordionSection {...getAccordionModelBySection('AdditionalCategories')} />
            case 'RentalAccordion':
                return <CategoryAccordionSection {...getAccordionModelBySection('RentalCategories')} isRentalAccordion={true} />
            case 'AdditionalAccordion':
                return <CategoryAccordionSection {...getAccordionModelBySection('AdditionalCategoriesAltLayout')} />
            case 'ListingInputBanner':
                return (
                    <section className='listing-input-banner'>
                        <div className='image-container'>
                            <img src={props.ListingInputBanner.ImageURL} alt='Input Image' />
                        </div>
                        <div className='blurb-container'>
                            <div className='blurb-label'>{props.ListingInputBanner.Title}</div>
                            <p>{props.ListingInputBanner.Desciption}</p>
                            <a className='ts-button' href={props.ListingInputBanner.InputURL} target='_blank'>{translator.translate('sandhills.sellyourequipment')} <span className='visually-hidden'>({translator.translate('sandhills.opensinanewtab')})</span></a>
                        </div>
                    </section>
                );
            case 'ATUpcomingAuctions':
                return (
                    <section className="at-upcoming-auctions">
                        < div className="main grey-bg home-page-featured-sellers exceed-width" >
                            <div className="grey-container">
                                <h2 className="section-title">
                                    <span className="section-title-text">{translator.translate('sandhills.upcomingauctions')}</span>
                                    <a className='ts-button view-all-link' href={routes.UpcomingEvents}>
                                        <strong>{translator.translate('sandhills.tradepubwebsite.mvc.viewall')}</strong>
                                    </a>
                                </h2>
                                {props.UpcomingAuctionFeaturedSellersList.AuctionDates.map((vmDate, index) => (
                                    <FeaturedSellerAuctionDate
                                        {...vmDate}
                                    />
                                ))}
                            </div>
                        </div >
                    </section>
                );
            case 'AuctionBoardHeader':
                return (
                    <>
                        {props.LiveBoardHeaders?.ActiveBoards?.length > 0 && 
                            <section className="at-auctionboard-header">
                                <AuctionBoardLiveBoardHeader {...props.LiveBoardHeaders} />
                            </section>
                        }
                    </>
                );
            case 'QuoteInstructions':
                return (
                    <section className='quote-instruction-section'>
                        <QuoteInstructions RequestAQuoteInstructions={props.RequestAQuoteInstructions} />
                    </section>
                );
            case 'CharterCategoryGrid':
                return (
                    <section className='alternate-category-grid'>
                        <div className='container'>
                            <DoubleClickAd
                                {...props.DoubleClickAdCollection}
                                position={bannerAdPositions.Middle}
                                ad={middleBannerAd}
                                contained
                            />
                            <h2>{translator.translate('sandhills.tradesites.mvc.chooseyourcharteraircraft')}</h2>
                            {getCategoryIconGrid(true)}
                            <DrilldownTabPanel GetCountryDropDown={props.GetCountryDropDown} GetCountryDropDownByCode={props.GetCountryDropDownByCode} IsMetric={props.IsMetric} />
                        </div>
                    </section>);
            default:
                return (<></>);
        }
    };

    return (
        <>
            {props.FirstTimeUserInstructionModel && <FirstTimeUserInstructionBanner {...props.FirstTimeUserInstructionModel} />}
            {<div className={'home-content' + (windowSize.isMobile ? '-mobile' : '')}>
                {props.HomePageSectionList.map((section) => <Fragment key={section}>{renderSection(section)}</Fragment>)}
            </div>}
        </>
    );
};

export default HomePageView;